import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Spin, 
  message, 
  Typography, 
  Button, 
  Badge, 
  Tag, 
  Space, 
  Flex, 
  Alert, 
  Empty
} from 'antd';
import { DragOutlined, UserOutlined, NumberOutlined, SaveOutlined, TableOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { api } from '@/api';
import { logger } from '@/utils/logger';
import { TournamentCategory } from '@/types/api.types';
import { compareTournamentCategories } from '@/utils/tournamentCategorySort';

const { Title, Text } = Typography;

interface CategoryWithStats {
  category: TournamentCategory;
  participantCount: number;
  matchCount: number;
  order: number;
  /** Минимальный номер боя в категории (для сортировки как в PDF, если нет categoryOrderOnTatami) */
  minMatchNumber: number | null;
}

interface TatamiData {
  tatamiNumber: number;
  categories: CategoryWithStats[];
  totalMatches: number;
}

interface TatamiDistributionProps {
  tournamentId: number;
}

// Компонент для перетаскиваемой категории
const DraggableCategoryItem: React.FC<{
  category: CategoryWithStats;
  tatamiNumber: number;
  onDragStart: (categoryId: number, tatamiNumber: number) => void;
  onDragEnd: () => void;
}> = ({ category, tatamiNumber, onDragStart, onDragEnd }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = (e: React.DragEvent) => {
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    // tournament_categories.id (tc.id), required for save endpoint
    const tcId = category.category?.id ?? (category as any).id;
    e.dataTransfer.setData('categoryId', String(tcId));
    e.dataTransfer.setData('sourceTatami', tatamiNumber.toString());
    onDragStart(tcId, tatamiNumber);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    onDragEnd();
  };

  return (
    <Card
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      size="small"
      className={`cursor-move transition-all duration-300 ${
        isDragging ? 'opacity-50 scale-95' : ''
      }`}
      style={{
        borderColor: isDragging ? '#60a5fa' : undefined,
        borderWidth: 2
      }}
      hoverable
    >
      <Flex vertical gap={8} style={{ flex: 1, minWidth: 0 }}>
        <Flex align="center" gap={4}>
          <DragOutlined className="text-gray-400" />
          <Text strong ellipsis style={{ margin: 0 }}>
            {category.category?.name ?? (category as any).name ?? '—'}
          </Text>
        </Flex>
        
        <Space size={4} direction="vertical" style={{ width: '100%' }}>
          <Tag icon={<UserOutlined />} color="blue">
            {category.participantCount} участников
          </Tag>
          <Tag icon={<NumberOutlined />} color="green">
            {category.matchCount} боев
          </Tag>
        </Space>
      </Flex>
    </Card>
  );
};

// Компонент для ячейки категории в столбце татами
const CategoryCell: React.FC<{
  category: CategoryWithStats | null;
  tatamiNumber: number;
  rowIndex: number;
  onCategoryDrop: (categoryId: number, sourceTatami: number, targetTatami: number, rowIndex: number) => void;
  onCategoryDragStart: (categoryId: number, tatamiNumber: number) => void;
  onCategoryDragEnd: () => void;
  isDragging: boolean;
  isLastColumn?: boolean;
}> = ({ category, tatamiNumber, rowIndex, onCategoryDrop, onCategoryDragStart, onCategoryDragEnd, isLastColumn = false }) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const categoryId = parseInt(e.dataTransfer.getData('categoryId'));
    const sourceTatami = parseInt(e.dataTransfer.getData('sourceTatami'));
    
    if (categoryId && sourceTatami) {
      onCategoryDrop(categoryId, sourceTatami, tatamiNumber, rowIndex);
    }
  };

  if (category) {
    return (
      <td
        className={`p-3 transition-all duration-300 relative border-l-2 border-r-2 ${
          isDragOver 
            ? 'bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-400 border-dashed' 
            : 'bg-white hover:bg-gray-50'
        } ${!isLastColumn ? 'border-r-4 border-gray-400' : 'border-r-4 border-gray-400'}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{ 
          minHeight: '120px', 
          verticalAlign: 'top',
          boxShadow: isDragOver ? 'inset 0 0 0 2px rgba(59, 130, 246, 0.3)' : 'none',
          borderLeftColor: 'rgba(156, 163, 175, 0.3)',
          borderRightColor: '#9ca3af',
          paddingLeft: '32px',
          paddingRight: '32px'
        }}
      >
        {/* Вертикальная разделительная линия справа */}
        {!isLastColumn && (
          <div className="absolute right-0 top-0 bottom-0 w-1 bg-gray-400/60 shadow-md"></div>
        )}
        <DraggableCategoryItem
          category={category}
          tatamiNumber={tatamiNumber}
          onDragStart={onCategoryDragStart}
          onDragEnd={onCategoryDragEnd}
        />
      </td>
    );
  }

  return (
    <td
      className={`p-3 transition-all duration-300 relative border-l-2 border-r-2 ${
        isDragOver 
          ? 'bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-400 border-dashed' 
          : 'bg-gray-50/50'
      } ${!isLastColumn ? 'border-r-4 border-gray-400' : 'border-r-4 border-gray-400'}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{ 
        minHeight: '120px', 
        verticalAlign: 'top',
        boxShadow: isDragOver ? 'inset 0 0 0 2px rgba(59, 130, 246, 0.3)' : 'none',
        borderLeftColor: 'rgba(156, 163, 175, 0.3)',
        borderRightColor: '#9ca3af',
        paddingLeft: '32px',
        paddingRight: '32px'
      }}
    >
      {/* Вертикальная разделительная линия справа */}
      {!isLastColumn && (
        <div className="absolute right-0 top-0 bottom-0 w-1 bg-gray-400/60 shadow-md"></div>
      )}
      <Empty
        image={<DragOutlined className="text-4xl text-gray-300" />}
        description={<Text type="secondary" className="text-xs">Перетащите сюда</Text>}
        style={{ padding: '20px 0' }}
      />
    </td>
  );
};

export const TatamiDistribution: React.FC<TatamiDistributionProps> = ({ tournamentId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tatamiData, setTatamiData] = useState<TatamiData[]>([]);
  const tatamiDataRef = React.useRef<TatamiData[]>([]);
  const [draggingCategory, setDraggingCategory] = useState<{ categoryId: number; tatamiNumber: number } | null>(null);
  const [hasMatches, setHasMatches] = useState(false);

  React.useEffect(() => {
    tatamiDataRef.current = tatamiData;
  }, [tatamiData]);

  // Проверяем наличие матчей
  useEffect(() => {
    const checkMatches = async () => {
      try {
        const response = await api.get(`/tournaments/${tournamentId}/matches`);
        const matches = response.data?.data || response.data || [];
        setHasMatches(matches.length > 0);
      } catch (error) {
        logger.error('Error checking matches:', error);
      }
    };
    checkMatches();
  }, [tournamentId]);

  // Загружаем данные о распределении
  useEffect(() => {
    loadDistribution();
  }, [tournamentId, hasMatches]);

  const loadDistribution = async (bustCache = false) => {
    if (!hasMatches) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const cacheBuster = bustCache ? `?_t=${Date.now()}` : '';
      // Загружаем категории (после сохранения — с cache-bust, чтобы получить актуальные categoryOrderOnTatami)
      const categoriesResponse = await api.get(`/tournaments/${tournamentId}/categories${cacheBuster}`);
      const categories: TournamentCategory[] = categoriesResponse.data?.data || categoriesResponse.data || [];

      // Загружаем матчи (актуальные tatamiNumber и matchNumber)
      const matchesResponse = await api.get(`/tournaments/${tournamentId}/matches${cacheBuster}`);
      const matches = matchesResponse.data?.data || matchesResponse.data || [];

      // Подсчитываем статистику по категориям (в т.ч. minMatchNumber для сортировки как в PDF)
      const categoryStats = new Map<number, { participantCount: number; matchCount: number; tatamiNumber: number | null; order: number; minMatchNumber: number | null }>();
      
      categories.forEach((category, index) => {
        const categoryMatches = matches.filter((m: any) => 
          (m.categoryId || (m.category as any)?.id) === category.id
        );
        
        const participantIds = new Set<number>();
        categoryMatches.forEach((match: any) => {
          if (match.participant1Id) participantIds.add(match.participant1Id);
          if (match.participant2Id) participantIds.add(match.participant2Id);
        });

        const firstMatch = categoryMatches[0];
        const tatamiNumber = firstMatch?.tatamiNumber || null;
        const matchNumbers = categoryMatches.map((m: any) => m.matchNumber).filter((n: any) => n != null && typeof n === 'number') as number[];
        const minMatchNumber = matchNumbers.length > 0 ? Math.min(...matchNumbers) : null;

        categoryStats.set(category.id, {
          participantCount: participantIds.size,
          matchCount: categoryMatches.length,
          tatamiNumber,
          order: index,
          minMatchNumber
        });
      });

      // Группируем по татами (исключаем пустые категории)
      const tatamiMap = new Map<number, CategoryWithStats[]>();
      const unassignedCategories: CategoryWithStats[] = [];

      categories.forEach((category) => {
        const stats = categoryStats.get(category.id);
        if (!stats) return;

        // Пропускаем пустые категории (без матчей или без участников)
        if (stats.matchCount === 0 || stats.participantCount === 0) {
          return;
        }

        const categoryWithStats: CategoryWithStats = {
          category,
          participantCount: stats.participantCount,
          matchCount: stats.matchCount,
          order: stats.order,
          minMatchNumber: stats.minMatchNumber
        };

        if (stats.tatamiNumber) {
          if (!tatamiMap.has(stats.tatamiNumber)) {
            tatamiMap.set(stats.tatamiNumber, []);
          }
          tatamiMap.get(stats.tatamiNumber)!.push(categoryWithStats);
        } else {
          unassignedCategories.push(categoryWithStats);
        }
      });

      // Порядок: сначала categoryOrderOnTatami (сохранённый в БД), иначе minMatchNumber (как в PDF)
      tatamiMap.forEach((categories) => {
        categories.sort((a, b) => {
          const orderA = a.category?.categoryOrderOnTatami ?? null;
          const orderB = b.category?.categoryOrderOnTatami ?? null;
          if (orderA != null && orderB != null) return orderA - orderB;
          if (orderA != null) return -1;
          if (orderB != null) return 1;
          const minA = a.minMatchNumber ?? Infinity;
          const minB = b.minMatchNumber ?? Infinity;
          if (minA !== minB) return minA - minB;
          return compareTournamentCategories(a.category, b.category) || a.order - b.order;
        });
      });

      // Определяем максимальный номер татами
      const maxTatami = Math.max(...Array.from(tatamiMap.keys()), 0);
      const tatamiCount = maxTatami || 1;

      // Создаем массив данных по татами
      const tatamiArray: TatamiData[] = [];
      for (let i = 1; i <= tatamiCount; i++) {
        const categories = tatamiMap.get(i) || [];
        const totalMatches = categories.reduce((sum, c) => sum + c.matchCount, 0);
        tatamiArray.push({
          tatamiNumber: i,
          categories,
          totalMatches
        });
      }

      unassignedCategories.sort((a, b) => {
        const orderA = a.category?.categoryOrderOnTatami ?? null;
        const orderB = b.category?.categoryOrderOnTatami ?? null;
        if (orderA != null && orderB != null) return orderA - orderB;
        if (orderA != null) return -1;
        if (orderB != null) return 1;
        const minA = a.minMatchNumber ?? Infinity;
        const minB = b.minMatchNumber ?? Infinity;
        if (minA !== minB) return minA - minB;
        return compareTournamentCategories(a.category, b.category) || a.order - b.order;
      });

      // Если есть нераспределенные категории, добавляем их в первый татами
      if (unassignedCategories.length > 0) {
        if (tatamiArray.length === 0) {
          tatamiArray.push({
            tatamiNumber: 1,
            categories: [],
            totalMatches: 0
          });
        }
        tatamiArray[0].categories.push(...unassignedCategories);
        tatamiArray[0].totalMatches += unassignedCategories.reduce((sum, c) => sum + c.matchCount, 0);
      }

      setTatamiData(tatamiArray);
    } catch (error) {
      logger.error('Error loading distribution:', error);
      message.error('Ошибка загрузки распределения по татами');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryDragStart = (categoryId: number, tatamiNumber: number) => {
    setDraggingCategory({ categoryId, tatamiNumber });
  };

  const handleCategoryDragEnd = () => {
    setDraggingCategory(null);
  };

  const handleCategoryDrop = (categoryId: number, sourceTatami: number, targetTatami: number, rowIndex: number) => {
    const sourceTatamiIndex = tatamiData.findIndex(t => t.tatamiNumber === sourceTatami);
    const targetTatamiIndex = tatamiData.findIndex(t => t.tatamiNumber === targetTatami);
    if (sourceTatamiIndex === -1 || targetTatamiIndex === -1) return;

    const categoryIndex = tatamiData[sourceTatamiIndex].categories.findIndex(
      c => c.category.id === categoryId
    );
    if (categoryIndex === -1) return;

    const movedCategory = tatamiData[sourceTatamiIndex].categories[categoryIndex];

    if (sourceTatami === targetTatami) {
      // Перемещение внутри одного татами: вырезаем и вставляем в новую позицию
      const categories = [...tatamiData[sourceTatamiIndex].categories];
      categories.splice(categoryIndex, 1);
      const insertAt = Math.min(rowIndex, categories.length);
      categories.splice(insertAt, 0, movedCategory);
      const newTatamiData = tatamiData.map((t, i) =>
        i === sourceTatamiIndex ? { ...t, categories } : { ...t, categories: [...t.categories] }
      );
      setTatamiData(newTatamiData);
      return;
    }

    // Разные татами: убираем из source, вставляем в target
    const newTatamiData = tatamiData.map((t, i) => {
      if (i === sourceTatamiIndex) {
        const categories = t.categories.filter((_, idx) => idx !== categoryIndex);
        return { ...t, categories, totalMatches: t.totalMatches - movedCategory.matchCount };
      }
      if (i === targetTatamiIndex) {
        const insertAt = Math.min(rowIndex, t.categories.length);
        const categories = [...t.categories];
        categories.splice(insertAt, 0, movedCategory);
        return { ...t, categories, totalMatches: t.totalMatches + movedCategory.matchCount };
      }
      return { ...t, categories: [...t.categories] };
    });
    setTatamiData(newTatamiData);
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Берём актуальный порядок из ref (на случай асинхронности state)
      const currentData = tatamiDataRef.current;
      const distribution: Array<{ categoryId: number; tatamiNumber: number; order: number }> = [];

      currentData.forEach((tatami) => {
        tatami.categories.forEach((catWithStats, index) => {
          const tcId = catWithStats.category?.id ?? (catWithStats as any).id;
          if (tcId == null) return;
          distribution.push({
            categoryId: tcId,
            tatamiNumber: tatami.tatamiNumber,
            order: index
          });
        });
      });

      if (distribution.length === 0) {
        message.warning('Нет категорий для сохранения');
        return;
      }

      await api.post(`/tournaments/${tournamentId}/tatami-distribution`, {
        distribution
      });

      message.success('Распределение сохранено и номера боев пересчитаны');

      // Перезагружаем с cache-bust, чтобы порядок и нумерация отобразились по данным с сервера
      await loadDistribution(true);
    } catch (error: any) {
      logger.error('Error saving distribution:', error);
      if (error?.response?.data) {
        console.error('Server response (tatami-distribution):', error.response.data);
      }
      const serverMessage = error?.response?.data?.message;
      message.error(serverMessage || error?.message || 'Ошибка сохранения распределения');
    } finally {
      setSaving(false);
    }
  };


  if (!hasMatches) {
    return (
      <Card>
        <Empty
          image={<InfoCircleOutlined className="text-6xl text-gray-400" />}
          description={
            <div>
              <Title level={4}>Сетка турнира еще не сформирована</Title>
              <Text type="secondary">
                Сначала необходимо сформировать сетку турнира, чтобы распределить категории по татами
              </Text>
            </div>
          }
        />
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 0' }}>
          <Spin size="large" />
          <Text type="secondary" style={{ marginTop: 16 }}>Загрузка распределения...</Text>
        </div>
      </Card>
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Заголовок */}
      <Card>
        <Flex justify="space-between" align="center" wrap="wrap" gap={16}>
          <Title level={2} style={{ margin: 0 }}>
            Распределение категорий по татами
          </Title>
          
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            size="large"
          >
            Сохранить изменения
          </Button>
        </Flex>
      </Card>

      {/* Подсказка */}
      <Alert
        message="Как использовать"
        description="Перетащите категории между татами для изменения распределения. Порядок категорий внутри татами определяет последовательность боев. После сохранения номера боев будут автоматически пересчитаны."
        type="info"
        icon={<InfoCircleOutlined />}
        showIcon
      />

      {/* Таблица с татами как столбцами */}
      <Card>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {tatamiData.map((tatami, index) => {
                const gradientColors = [
                  'linear-gradient(135deg, #93c5fd 0%, #60a5fa 100%)',
                  'linear-gradient(135deg, #86efac 0%, #4ade80 100%)',
                  'linear-gradient(135deg, #c4b5fd 0%, #a78bfa 100%)',
                  'linear-gradient(135deg, #fdba74 0%, #fb923c 100%)',
                  'linear-gradient(135deg, #a5b4fc 0%, #818cf8 100%)',
                ];
                const colorIndex = (tatami.tatamiNumber - 1) % gradientColors.length;
                const isLast = index === tatamiData.length - 1;
                
                return (
                  <th
                    key={tatami.tatamiNumber}
                    className={`p-4 text-white font-bold text-center relative ${
                      index === 0 ? 'border-l-4' : 'border-l-2'
                    } border-r-4 border-gray-400`}
                    style={{
                      background: gradientColors[colorIndex],
                      position: 'relative',
                      boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)',
                      borderLeftColor: index === 0 ? '#9ca3af' : 'rgba(255, 255, 255, 0.3)',
                      borderRightColor: '#9ca3af',
                      minWidth: '300px',
                      paddingLeft: '32px',
                      paddingRight: '32px',
                      marginRight: index < tatamiData.length - 1 ? '16px' : '0'
                    }}
                  >
                    {/* Вертикальная разделительная линия справа */}
                    {!isLast && (
                      <div className="absolute right-0 top-0 bottom-0 w-1 bg-gray-400/50 shadow-lg"></div>
                    )}
                    <div className="flex flex-col items-center gap-2">
                      <div className="flex items-center gap-2">
                        <TableOutlined className="text-xl" />
                        <Title level={4} className="mb-0 text-white">
                          Татами {tatami.tatamiNumber}
                        </Title>
                      </div>
                      <Badge 
                        count={tatami.totalMatches} 
                        showZero={false}
                        className="bg-white/20 backdrop-blur-sm border-white/30"
                        style={{ 
                          color: 'white',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          borderColor: 'rgba(255, 255, 255, 0.3)'
                        }}
                      >
                        <div className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                          <Text strong className="text-white">
                            боев
                          </Text>
                        </div>
                      </Badge>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {/* Находим максимальное количество категорий среди всех татами */}
            {(() => {
              const maxCategories = Math.max(...tatamiData.map(t => t.categories.length), 0);
              const rows: Array<Array<CategoryWithStats | null>> = [];
              
              // Создаем строки - каждая строка содержит категории для всех татами
              for (let rowIndex = 0; rowIndex < maxCategories; rowIndex++) {
                const row: Array<CategoryWithStats | null> = [];
                tatamiData.forEach(tatami => {
                  row.push(tatami.categories[rowIndex] || null);
                });
                rows.push(row);
              }
              
              return rows.map((row, rowIndex) => (
                <tr 
                  key={rowIndex} 
                  className="hover:bg-gray-50/30 transition-colors border-b-2 border-gray-200"
                >
                  {row.map((category, colIndex) => {
                    const tatami = tatamiData[colIndex];
                    const isLast = colIndex === row.length - 1;
                    return (
                      <CategoryCell
                        key={`${tatami.tatamiNumber}-${rowIndex}`}
                        category={category}
                        tatamiNumber={tatami.tatamiNumber}
                        rowIndex={rowIndex}
                        onCategoryDrop={handleCategoryDrop}
                        onCategoryDragStart={handleCategoryDragStart}
                        onCategoryDragEnd={handleCategoryDragEnd}
                        isDragging={draggingCategory !== null}
                        isLastColumn={isLast}
                      />
                    );
                  })}
                </tr>
              ));
            })()}
            
            {/* Строка с итогами */}
            <tr className="bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 font-bold border-t-4 border-gray-400">
              {tatamiData.map((tatami, index) => (
                  <td
                    key={`total-${tatami.tatamiNumber}`}
                    className={`text-center border-l-2 border-r-4 border-gray-400`}
                    style={{
                      background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)',
                      borderLeftColor: index === 0 ? '#9ca3af' : 'rgba(156, 163, 175, 0.3)',
                      borderRightColor: '#9ca3af',
                      padding: '16px 32px'
                    }}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <NumberOutlined className="text-xl text-gray-700" />
                      <Text strong className="text-xl text-gray-900">
                        {tatami.totalMatches}
                      </Text>
                    </div>
                  </td>
                ))}
            </tr>
          </tbody>
        </table>
        </div>
      </Card>

      {/* Модальное окно для переноса категории на другой татами */}
    </Space>
  );
};
